import type React from 'react';
import { createContext, useContext, useMemo, useSyncExternalStore } from 'react';

export interface LayoutDimensions {
    minWidth?: number;
    maxWidth?: number;
}

export type ScreenType = 'mobile' | 'tablet' | 'desktop';

export const MOBILE_BREAKPOINT = 768;
export const TABLET_BREAKPOINT = 1024;
export const DEKSTOP_BREAKPOINT = Number.MAX_VALUE;

/*
 * Hook to test the layout size.
 */
export function useScreenLayout(query: LayoutDimensions): boolean {
    const mediaQueryList = useMemo(() => {
        const mediaQueryString = [
            query.maxWidth ? `(max-width: ${query.maxWidth}px)` : null,
            query.minWidth ? `(min-width: ${query.minWidth}px)` : null,
        ]
            .filter(Boolean)
            .join(' and ');

        return window.matchMedia(mediaQueryString);
    }, [query.minWidth, query.maxWidth]);

    return useSyncExternalStore(
        (callback) => {
            mediaQueryList.addEventListener('change', callback);
            return () => mediaQueryList.removeEventListener('change', callback);
        },
        () => (mediaQueryList ? mediaQueryList.matches : false)
    );
}

// Single context (provided in the UIKitProvider) to avoid subscribed multiple times to the same media queries
export const ScreenTypeContext = createContext<ScreenType>('desktop');
export const ScreenTypeProvider = ({ children }: { children: React.ReactNode }) => {
    const mobile = useScreenLayout({
        maxWidth: MOBILE_BREAKPOINT,
    });

    const tablet = useScreenLayout({
        maxWidth: TABLET_BREAKPOINT,
    });

    const screenType = mobile ? 'mobile' : tablet ? 'tablet' : 'desktop';

    return <ScreenTypeContext.Provider value={screenType}>{children}</ScreenTypeContext.Provider>;
};

/*
 * Helper to get the kind of device we are on.
 */
export function useScreenType(): {
    screenType: ScreenType;
    isMobile: boolean;
    isTablet: boolean;
    isDesktop: boolean;
    isMobileOrTablet: boolean;
} {
    const screenType = useContext(ScreenTypeContext);
    const isMobile = screenType === 'mobile';
    const isTablet = screenType === 'tablet';
    const isDesktop = screenType === 'desktop';

    return {
        screenType,
        isMobile,
        isTablet,
        isDesktop,
        isMobileOrTablet: isMobile || isTablet,
    };
}
